/**
 * The receipts against a purchase order: every receivable that names it, newest first.
 *
 * ED, 2026-10-30: "Add a Receipts list on the PO page." Each receipt is read under the reader's own
 * sight (/t1/v1/order_receipts): a visible one shows its status, the date and who received, the
 * packing slip, how many lines received something and the sum received, and links to its page; one
 * the reader may not open is its ID, grey, no link. Only a RELEASED receipt counts toward the order's
 * received quantities (phase 2's write-back), and the row says so.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, Typography} from "@mui/material";
import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";

type Row = {ObjectUSID: string; RevisionUSID?: string; ObjectID?: string | null; Name?: string | null; Status?: string | null;
            Visible: boolean; ReceivedDate?: string | null; ReceivedBy?: string | null; PackingSlipNumber?: string | null;
            LinesReceived?: number; QuantityReceived?: any; Counted?: boolean};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

export default function OrderReceiptsPane({objectUSID}: {objectUSID: string}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const navigate = useNavigate();
    const [rows, setRows] = useState<Row[] | null>(null);
    const [hidden, setHidden] = useState(0);
    const [problem, setProblem] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!objectUSID) return;
        api.get("t1/v1/order_receipts", {params: {PurchaseOrderObjectUSID: objectUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setRows(listOf<Row>(message?.ListOfOrderReceipt?.OrderReceipt));
                setHidden(message.HiddenCount ?? 0);
                setProblem(null);
            })
            .catch((failure: any) => { setRows([]); setProblem(failure?.response?.data?.Cause ?? "The receipts could not be read."); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);
    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px"}}>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "4px"}}>
                Receipts{rows ? ` (${rows.length})` : ""}
            </Typography>
            <Typography sx={{fontSize: 12.5, color: Grey7, marginBottom: "10px"}}>
                Only a released receipt counts toward the lines' received quantities. A new receipt is raised from Receivables, naming this order.
            </Typography>
            {problem && <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>{problem}</Typography>}
            {rows === null && <Typography sx={{fontSize: 13, color: Grey7}}>Reading…</Typography>}
            {rows && rows.length === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>Nothing has been received against this order yet.</Typography>
            )}
            {rows && rows.length > 0 && (
                <Box sx={{display: "grid", gridTemplateColumns: "1.1fr 2.2fr 1fr 1fr 1.4fr 1fr 0.8fr 0.9fr", gap: "6px 12px", alignItems: "center"}}>
                    {["ID", "Name", "Status", "Received", "By", "Packing slip", "Lines", "Quantity"].map((head, index) => (
                        <Typography key={`${head}${index}`} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>
                            {head}
                        </Typography>
                    ))}
                    {rows.map(row => row.Visible ? (
                        <React.Fragment key={row.ObjectUSID}>
                            <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer"}}
                                        onClick={() => row.RevisionUSID && navigate(`/object/${row.RevisionUSID}`)}>{row.ObjectID ?? "—"}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey17}}>{row.Name ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: row.Counted ? Grey17 : Grey6}}
                                        title={row.Counted ? "Counted toward the order" : "Not yet released, so not counted"}>
                                {row.Status ?? "—"}
                            </Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.ReceivedDate ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.ReceivedBy ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.PackingSlipNumber ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.LinesReceived ?? 0}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.QuantityReceived ?? 0}</Typography>
                        </React.Fragment>
                    ) : (
                        <React.Fragment key={row.ObjectUSID}>
                            <Typography sx={{fontSize: 13, color: Grey6}} title="This receipt is not visible to you.">{row.ObjectID ?? "—"}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic", gridColumn: "span 7"}}>Not visible to you</Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
            {hidden > 0 && (
                <Typography sx={{fontSize: 12.5, color: Grey7, marginTop: "8px"}}>
                    {hidden} {hidden === 1 ? "receipt is" : "receipts are"} not visible to you.
                </Typography>
            )}
        </Box>
    );
}
